import React from "react";
import {
  AbsoluteFill,
  Audio,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
  Easing,
} from "remotion";
import { AtmosphericParticles, DynamicSubtitle } from "./components";

export const KyuStoreMaster1MinPlus: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Palette: Clean Esport Studio Light Theme
  const bgCanvas = "#F8F6FE";
  const bgCard = "#FFFFFF";
  const kyuPurple = "#5D3FD3";
  const kyuDark = "#120A2E";
  const kyuLavender = "#F3E8FF";
  const accentGold = "#FFB020";
  const textDark = "#0F172A";
  const textMuted = "#64748B";
  const emeraldSuccess = "#10B981";
  const emeraldLight = "#ECFDF5";
  const coralAlert = "#EF4444";
  const coralLight = "#FEF2F2";
  const coralBorder = "#FECACA";

  const EASE = Easing.bezier(0.16, 1, 0.3, 1);

  const sp = (delay: number, damping = 14, stiffness = 125) =>
    spring({
      frame: frame - delay,
      fps,
      config: { damping, stiffness, mass: 1.0 },
    });

  // Ambient micro-motion float
  const floatY = Math.sin(frame / 22) * 4.5;

  // ==========================================
  // DYNAMIC SUBTITLES TIMELINE (2400 frames = 80s @ 30fps)
  // Strictly anchored at safe band bottom: 400px
  // ==========================================
  const SUBTITLES = [
    { from: 0, to: 155, text: "Kamu yang tiap malam begadang main solo rank, push bintang sampai Mythical Glory...", highlight: "push bintang sampai Mythical Glory" },
    { from: 155, to: 270, text: "...pasti tahu betapa beratnya perjuangan bangun akun Mobile Legends.", highlight: "perjuangan bangun akun" },
    { from: 270, to: 365, text: "Ratusan jam main, win streak berdarah-darah, tapi jangan sampai semua itu hancur sia-sia...", highlight: "jangan sampai hancur sia-sia" },
    { from: 365, to: 495, text: "Banyak kejadian mengerikan: login ke game, tiba-tiba ada notifikasi merah dari Moonton...", highlight: "notifikasi merah dari Moonton" },
    { from: 495, to: 615, text: "...diamond jadi minus ribuan, akun gak bisa masuk ranked, dan terancam banned permanen!", highlight: "diamond jadi minus ribuan" },
    { from: 615, to: 740, text: "Itu ulah penjual nakal yang minta login password atau pakai sistem refund carding ilegal.", highlight: "refund carding ilegal" },
    { from: 740, to: 870, text: "Di Kyu Store, keamanan akunmu adalah nomor satu. 100% resmi, legal, dan bergaransi anti-minus.", highlight: "100% resmi, legal, dan bergaransi" },
    { from: 870, to: 1010, text: "Kami gak pernah minta email atau password kamu—cukup User ID dan Zone ID saja.", highlight: "cukup User ID dan Zone ID" },
    { from: 1010, to: 1100, text: "Semua transaksi langsung diproses otomatis via Moonton Direct API resmi.", highlight: "Moonton Direct API resmi" },
    { from: 1100, to: 1240, text: "Mau hemat maksimal? Ambil paket Weekly Diamond Pass.", highlight: "Weekly Diamond Pass" },
    { from: 1240, to: 1360, text: "Modalnya cuma dua puluh tujuh ribuan, langsung dapat total dua ratus dua puluh diamond...", highlight: "dua puluh tujuh ribuan" },
    { from: 1360, to: 1460, text: "...plus bonus berlimpah dan Starlight point setiap hari!", highlight: "bonus berlimpah setiap hari" },
    { from: 1460, to: 1610, text: "Bayar instan pakai QRIS semua m-banking dan e-wallet...", highlight: "QRIS semua m-banking dan e-wallet" },
    { from: 1610, to: 1740, text: "...diamond langsung meluncur masuk ke akunmu dalam hitungan nol koma delapan detik!", highlight: "nol koma delapan detik!" },
    { from: 1740, to: 1880, text: "Tanpa nunggu admin balas chat, tanpa drama.", highlight: "Tanpa nunggu admin, tanpa drama" },
    { from: 1880, to: 2040, text: "Jangan gadaikan akun kesayanganmu ke seller abal-abal.", highlight: "Jangan gadaikan akun kesayanganmu" },
    { from: 2040, to: 2400, text: "Jaga akun tetap aman, top up resmi hanya di kyustore.com!", highlight: "kyustore.com" },
  ];

  // ==========================================
  // 6 SCENES TIMELINE
  // Scene 1: The Mythic Stake (Frame 0 - 365)
  // Scene 2: The Tragedy & Threat (Frame 365 - 740)
  // Scene 3: Kyu Store Safe Haven (Frame 740 - 1100)
  // Scene 4: Weekly Diamond Pass 3D (Frame 1100 - 1460)
  // Scene 5: Instant QRIS & 0.8s Auto-Inject (Frame 1460 - 1880)
  // Scene 6: Victory Payoff & Outro CTA (Frame 1880 - 2400)
  // ==========================================

  // --- Dynamic 3D Camera Orbit & Tilt ---
  const camOrbitY = Math.sin(frame / 32) * 4.5;
  const camOrbitX = Math.cos(frame / 36) * 2.8;

  // Camera Dolly Push-ins on scene punchlines
  const pushScene4 = interpolate(frame, [1220, 1260], [0, 80], { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: EASE });
  const pushScene6 = interpolate(frame, [2030, 2070], [0, 95], { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: EASE });
  const currentPushZ = pushScene4 + pushScene6;

  // Specular light sweep loop
  const sweepProgress = interpolate((frame % 160), [20, 80], [-120, 220], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: EASE,
  });

  // --- Scene 1 Transitions (0 - 365) ---
  const s1In = sp(6, 14, 115);
  const s1Y = interpolate(s1In, [0, 1], [60, 0]);
  const s1Scale = interpolate(s1In, [0, 1], [0.92, 1]);
  const s1Exit = interpolate(frame, [350, 368], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: EASE });
  const s1ExitY = interpolate(s1Exit, [0, 1], [0, -60]);
  const s1ExitOpacity = interpolate(s1Exit, [0, 0.8], [1, 0]);

  // --- Scene 2 Transitions (365 - 740) ---
  const s2In = sp(365, 14, 125);
  const s2Y = interpolate(s2In, [0, 1], [60, 0]);
  const s2Scale = interpolate(s2In, [0, 1], [0.9, 1]);
  const s2Glitch = frame >= 480 && frame <= 510 ? Math.sin(frame * 2.8) * 3 : 0;
  const s2Exit = interpolate(frame, [725, 742], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: EASE });
  const s2ExitY = interpolate(s2Exit, [0, 1], [0, -60]);
  const s2ExitOpacity = interpolate(s2Exit, [0, 0.8], [1, 0]);

  // --- Scene 3 Transitions (740 - 1100) ---
  const s3In = sp(740, 14, 120);
  const s3Y = interpolate(s3In, [0, 1], [60, 0]);
  const s3Scale = interpolate(s3In, [0, 1], [0.9, 1]);
  const s3Exit = interpolate(frame, [1085, 1102], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: EASE });
  const s3ExitY = interpolate(s3Exit, [0, 1], [0, -60]);
  const s3ExitOpacity = interpolate(s3Exit, [0, 0.8], [1, 0]);

  // --- Scene 4 Transitions (1100 - 1460) ---
  const s4In = sp(1100, 14, 120);
  const s4Y = interpolate(s4In, [0, 1], [60, 0]);
  const s4Scale = interpolate(s4In, [0, 1], [0.9, 1]);
  const s4CursorProgress = interpolate(frame, [1300, 1340], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: EASE });
  const s4CursorX = interpolate(s4CursorProgress, [0, 1], [740, 520]);
  const s4CursorY = interpolate(s4CursorProgress, [0, 1], [520, 360]);
  const s4IsClicking = frame >= 1340 && frame <= 1355;
  const s4Exit = interpolate(frame, [1445, 1462], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: EASE });
  const s4ExitY = interpolate(s4Exit, [0, 1], [0, -60]);
  const s4ExitOpacity = interpolate(s4Exit, [0, 0.8], [1, 0]);

  // --- Scene 5 Transitions (1460 - 1880) ---
  const s5In = sp(1460, 14, 120);
  const s5Y = interpolate(s5In, [0, 1], [60, 0]);
  const s5Scale = interpolate(s5In, [0, 1], [0.9, 1]);
  const scanLaserY = Math.sin(frame / 6) * 55;
  const s5Exit = interpolate(frame, [1865, 1882], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: EASE });
  const s5ExitY = interpolate(s5Exit, [0, 1], [0, -60]);
  const s5ExitOpacity = interpolate(s5Exit, [0, 0.8], [1, 0]);

  // --- Scene 6 Transitions (1880 - 2400) ---
  const s6In = sp(1880, 13, 115);
  const s6Y = interpolate(s6In, [0, 1], [60, 0]);
  const s6Scale = interpolate(s6In, [0, 1], [0.9, 1]);
  const s6CursorProg = interpolate(frame, [2120, 2160], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: EASE });
  const s6CursorX = interpolate(s6CursorProg, [0, 1], [720, 480]);
  const s6CursorY = interpolate(s6CursorProg, [0, 1], [540, 380]);
  const s6IsClicking = frame >= 2160 && frame <= 2178;

  return (
    <AbsoluteFill
      style={{
        backgroundColor: bgCanvas,
        fontFamily: "'Rajdhani', -apple-system, sans-serif",
        color: textDark,
        overflow: "hidden",
      }}
    >
      {/* 1. Global Mastered Audio & Ducked BGM */}
      <Audio src={staticFile("kyustore_1min_full_mastered.mp3")} volume={1.0} />
      <Audio src={staticFile("bgm-clean.mp3")} volume={0.12} />

      {/* Frame-Accurate Micro-SFX */}
      {frame === 6 && <Audio src={staticFile("whoosh-short.mp3")} volume={0.65} />}
      {frame === 365 && <Audio src={staticFile("swoosh-quick.mp3")} volume={0.7} />}
      {frame === 740 && <Audio src={staticFile("whoosh.mp3")} volume={0.65} />}
      {frame === 780 && <Audio src={staticFile("chime.mp3")} volume={0.8} />}
      {frame === 1100 && <Audio src={staticFile("pop.mp3")} volume={0.7} />}
      {frame === 1342 && <Audio src={staticFile("click.mp3")} volume={0.8} />}
      {frame === 1460 && <Audio src={staticFile("swoosh-quick.mp3")} volume={0.7} />}
      {frame === 1520 && <Audio src={staticFile("ui-success.mp3")} volume={0.75} />}
      {frame === 1880 && <Audio src={staticFile("chime.mp3")} volume={0.85} />}
      {frame === 2162 && <Audio src={staticFile("click.mp3")} volume={0.85} />}

      {/* 2. Multi-Depth Background (Z: -80px) */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `
            radial-gradient(rgba(93, 63, 211, 0.08) 1.5px, transparent 1.5px),
            linear-gradient(to right, rgba(93, 63, 211, 0.035) 1px, transparent 1px)
          `,
          backgroundSize: "32px 32px, 64px 64px",
          pointerEvents: "none",
        }}
      />

      {/* Volumetric Center Ambient Glow */}
      <div
        style={{
          position: "absolute",
          top: "46%",
          left: "50%",
          width: 880,
          height: 880,
          transform: "translate(-50%, -50%)",
          background: "radial-gradient(circle, rgba(93, 63, 211, 0.14) 0%, rgba(248, 246, 254, 0) 70%)",
          filter: "blur(80px)",
          pointerEvents: "none",
        }}
      />

      {/* Top Corner HUD */}
      <div
        style={{
          position: "absolute",
          top: 80,
          left: 90,
          display: "flex",
          alignItems: "center",
          gap: 12,
          zIndex: 50,
        }}
      >
        <Img src={staticFile("kyu_logo.png")} style={{ height: 34, objectFit: "contain" }} />
        <div style={{ width: 1.5, height: 22, backgroundColor: "#CBD5E1" }} />
        <span
          style={{
            fontFamily: "'Chakra Petch', sans-serif",
            fontSize: 14,
            fontWeight: 700,
            letterSpacing: "0.12em",
            color: textMuted,
          }}
        >
          [ OFFICIAL MOONTON DISTRIBUTOR ]
        </span>
      </div>

      <div
        style={{
          position: "absolute",
          top: 80,
          right: 90,
          display: "flex",
          alignItems: "center",
          gap: 8,
          backgroundColor: bgCard,
          border: `1.5px solid ${emeraldSuccess}`,
          padding: "6px 18px",
          borderRadius: 20,
          boxShadow: "0 4px 15px rgba(16, 185, 129, 0.12)",
          zIndex: 50,
        }}
      >
        <div style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: emeraldSuccess, boxShadow: "0 0 8px #10B981" }} />
        <span
          style={{
            fontFamily: "'Chakra Petch', sans-serif",
            fontSize: 13,
            fontWeight: 700,
            color: emeraldSuccess,
            letterSpacing: "0.08em",
          }}
        >
          24/7 AUTO INJECT ACTIVE
        </span>
      </div>

      {/* ========================================================================= */}
      {/* 3D SPATIAL VIEWPORT CONTAINER (Perspective 1400px)                         */}
      {/* ========================================================================= */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          perspective: 1400,
          perspectiveOrigin: "50% 48%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "none",
        }}
      >
        {/* Master Spatial Stage (Preserve-3D) */}
        <div
          style={{
            position: "relative",
            width: 900,
            height: 1100,
            transformStyle: "preserve-3d",
            transform: `rotateY(${camOrbitY}deg) rotateX(${camOrbitX}deg) translateZ(${currentPushZ}px)`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* ================================================================ */}
          {/* SCENE 1: THE MYTHIC STAKE / WIN STREAK (Frame 0 - 365)           */}
          {/* ================================================================ */}
          {frame < 368 && (
            <div
              style={{
                transformStyle: "preserve-3d",
                transform: `translateY(${s1Y + s1ExitY + floatY}px) scale(${s1Scale})`,
                opacity: s1ExitOpacity,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <div style={{ textAlign: "center", marginBottom: 20, transform: "translateZ(40px)" }}>
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    backgroundColor: emeraldLight,
                    border: `1.5px solid ${emeraldSuccess}`,
                    padding: "6px 20px",
                    borderRadius: 20,
                    marginBottom: 12,
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={emeraldSuccess} strokeWidth="3">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  <span style={{ fontFamily: "'Chakra Petch', sans-serif", fontSize: 14, fontWeight: 700, color: emeraldSuccess, letterSpacing: "0.08em" }}>
                    PERJUANGAN PUSH RANK MYTHIC
                  </span>
                </div>
                <h1 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 66, lineHeight: 1.02, letterSpacing: "0.02em" }}>
                  RATUSAN JAM PUSH RANK MYTHIC, <br />
                  <span style={{ color: kyuPurple }}>JANGAN SAMPAI HANCUR KARENA TOP UP ABAL-ABAL!</span>
                </h1>
              </div>

              {/* 3D Showcase Card with Real Win Streak Match History */}
              <div
                style={{
                  position: "relative",
                  width: 860,
                  height: 640,
                  backgroundColor: "rgba(255, 255, 255, 0.94)",
                  borderRadius: 38,
                  border: `2.5px solid ${kyuPurple}`,
                  boxShadow: "0 35px 90px rgba(93, 63, 211, 0.2), 0 10px 30px rgba(0,0,0,0.06)",
                  transformStyle: "preserve-3d",
                  overflow: "visible",
                }}
              >
                {/* Specular Light Sweep */}
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    width: 140,
                    left: `${sweepProgress}%`,
                    background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.85), transparent)",
                    transform: "skewX(-25deg)",
                    pointerEvents: "none",
                    borderRadius: 38,
                  }}
                />
                {/* Real Gameplay Screenshot: Match History Win Streak */}
                <div style={{ position: "absolute", inset: 16, borderRadius: 28, overflow: "hidden", transform: "translateZ(10px)" }}>
                  <Img
                    src={staticFile("assets_library/gameplay/mlbb_match_history_winstreak.jpg")}
                    style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 40%" }}
                  />
                  <div style={{ position: "absolute", inset: 0, boxShadow: "inset 0 0 45px rgba(93, 63, 211, 0.4)" }} />
                </div>

                {/* Floating Mythical Glory Dragon Crest Badge (Left, z: 110px) */}
                <div
                  style={{
                    position: "absolute",
                    top: 60,
                    left: -35,
                    transform: "translateZ(110px) rotateY(12deg)",
                    backgroundColor: bgCard,
                    borderRadius: 24,
                    border: `2px solid ${accentGold}`,
                    padding: "16px 22px",
                    boxShadow: "0 20px 45px rgba(0,0,0,0.15)",
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    zIndex: 35,
                  }}
                >
                  <Img src={staticFile("assets_library/ranks/rank_mythical_glory_official.png")} style={{ width: 62, height: 62, objectFit: "contain" }} />
                  <div>
                    <div style={{ fontFamily: "'Chakra Petch', sans-serif", fontSize: 12, fontWeight: 700, color: textMuted }}>TIER TERTINGGI</div>
                    <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 26, color: accentGold }}>MYTHICAL GLORY 84★</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ================================================================ */}
          {/* SCENE 2: THE TRAGEDY & TRAP (Frame 365 - 740)                    */}
          {/* ================================================================ */}
          {frame >= 360 && frame < 742 && (
            <div
              style={{
                transformStyle: "preserve-3d",
                transform: `translateY(${s2Y + s2ExitY + floatY}px) scale(${s2Scale}) translateX(${s2Glitch}px)`,
                opacity: s2ExitOpacity,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <div style={{ textAlign: "center", marginBottom: 20, transform: "translateZ(40px)" }}>
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    backgroundColor: coralLight,
                    border: `1.5px solid ${coralBorder}`,
                    padding: "6px 20px",
                    borderRadius: 20,
                    marginBottom: 12,
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={coralAlert} strokeWidth="3">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  <span style={{ fontFamily: "'Chakra Petch', sans-serif", fontSize: 14, fontWeight: 700, color: coralAlert, letterSpacing: "0.08em" }}>
                    BAHAYA SELLER NAKAL & CARDING
                  </span>
                </div>
                <h1 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 66, lineHeight: 1.02, letterSpacing: "0.02em" }}>
                  DIAMOND MINUS RIBUAN & KENA BANNED? <br />
                  <span style={{ color: coralAlert }}>AKIBAT MINTA PASSWORD & REFUND ILEGAL!</span>
                </h1>
              </div>

              {/* Danger Card Mockup */}
              <div
                style={{
                  position: "relative",
                  width: 860,
                  height: 640,
                  backgroundColor: bgCard,
                  borderRadius: 38,
                  border: `2.5px solid ${coralBorder}`,
                  boxShadow: "0 35px 90px rgba(239, 68, 68, 0.16)",
                  padding: "36px 40px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  transformStyle: "preserve-3d",
                }}
              >
                {/* Minus Box Alert */}
                <div
                  style={{
                    backgroundColor: "#FFF5F5",
                    border: `2px dashed ${coralAlert}`,
                    borderRadius: 26,
                    padding: "26px 32px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 18, fontWeight: 700, color: textMuted }}>SALDO DIAMOND AKUN:</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 4 }}>
                      <span style={{ fontFamily: "'Anton', sans-serif", fontSize: 56, color: coralAlert }}>-1.500</span>
                      <svg width="36" height="36" viewBox="0 0 24 24" fill={coralAlert} stroke="#FFFFFF" strokeWidth="1.5">
                        <path d="M6 3h12l4 6-10 12L2 9Z" />
                        <path d="M11 3 8 9l4 12 4-12-3-6" fill="#FCA5A5" />
                        <path d="M2 9h20" stroke="#FFFFFF" strokeWidth="1.2" />
                      </svg>
                      <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 36, color: coralAlert }}>(MINUS)</span>
                    </div>
                  </div>
                  <div
                    style={{
                      border: `4px solid ${coralAlert}`,
                      color: coralAlert,
                      fontFamily: "'Bebas Neue', sans-serif",
                      fontSize: 34,
                      letterSpacing: "0.1em",
                      padding: "8px 24px",
                      borderRadius: 16,
                      transform: "rotate(-8deg)",
                    }}
                  >
                    SANKSI BANNED
                  </div>
                </div>

                {/* Form Trap Warning */}
                <div style={{ backgroundColor: "#FFF8F8", border: "1.5px solid #FEE2E2", borderRadius: 22, padding: "20px 24px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 16, fontWeight: 700, color: coralAlert }}>
                      CONTOH FORMAT SELLER PENIPUAN:
                    </span>
                    <span style={{ fontFamily: "'Chakra Petch', sans-serif", fontSize: 13, fontWeight: 700, color: coralAlert }}>
                      RAWAN PHISHING / HACK
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: 16 }}>
                    <div style={{ flex: 1, backgroundColor: "#FFFFFF", border: "1.5px solid #CBD5E1", padding: "12px 18px", borderRadius: 14 }}>
                      <span style={{ fontSize: 13, color: textMuted }}>Email Akun:</span>
                      <div style={{ fontSize: 16, fontWeight: 700 }}>player_mlbb@gmail.com</div>
                    </div>
                    <div style={{ flex: 1, backgroundColor: "#FFFFFF", border: `2px solid ${coralAlert}`, padding: "12px 18px", borderRadius: 14 }}>
                      <span style={{ fontSize: 13, color: coralAlert, fontWeight: 700 }}>Password Akun (JANGAN KASIH):</span>
                      <div style={{ fontSize: 16, fontWeight: 900, color: coralAlert }}>••••••••••••••</div>
                    </div>
                  </div>
                </div>

                {/* 3 Risk Checklist */}
                <div style={{ display: "flex", justifyContent: "space-between", borderTop: "1.5px solid #FEE2E2", paddingTop: 16 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 15, fontWeight: 800, color: coralAlert }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={coralAlert} strokeWidth="3">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                    <span>Rawan Hack Akun</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 15, fontWeight: 800, color: coralAlert }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={coralAlert} strokeWidth="3">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                    <span>Modus Refund Ilegal</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 15, fontWeight: 800, color: coralAlert }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={coralAlert} strokeWidth="3">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                    <span>Akun Suspend Permanen</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ================================================================ */}
          {/* SCENE 3: KYU STORE SAFE HAVEN / GUSION 3D (Frame 740 - 1100)     */}
          {/* ================================================================ */}
          {frame >= 735 && frame < 1102 && (
            <div
              style={{
                transformStyle: "preserve-3d",
                transform: `translateY(${s3Y + s3ExitY + floatY}px) scale(${s3Scale})`,
                opacity: s3ExitOpacity,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <div style={{ textAlign: "center", marginBottom: 20, transform: "translateZ(40px)" }}>
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    backgroundColor: emeraldLight,
                    border: `1.5px solid ${emeraldSuccess}`,
                    padding: "6px 20px",
                    borderRadius: 20,
                    marginBottom: 12,
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={emeraldSuccess} strokeWidth="3">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/>
                  </svg>
                  <span style={{ fontFamily: "'Chakra Petch', sans-serif", fontSize: 14, fontWeight: 700, color: emeraldSuccess, letterSpacing: "0.08em" }}>
                    SOLUSI 100% LEGAL & ANTI-MINUS RESMI
                  </span>
                </div>
                <h1 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 66, lineHeight: 1.02, letterSpacing: "0.02em" }}>
                  DI KYU STORE, KEAMANAN AKUN NOMOR SATU! <br />
                  <span style={{ color: kyuPurple }}>CUKUP USER ID & ZONE ID • TANPA PASSWORD!</span>
                </h1>
              </div>

              {/* 3D Showcase Card with Authentic Gusion In-Game Gameplay */}
              <div
                style={{
                  position: "relative",
                  width: 860,
                  height: 640,
                  backgroundColor: "rgba(255, 255, 255, 0.94)",
                  borderRadius: 38,
                  border: `2.5px solid ${kyuPurple}`,
                  boxShadow: "0 35px 90px rgba(93, 63, 211, 0.2), 0 10px 30px rgba(0,0,0,0.06)",
                  transformStyle: "preserve-3d",
                }}
              >
                {/* Gameplay Viewport */}
                <div style={{ position: "absolute", inset: 16, borderRadius: 28, overflow: "hidden", transform: "translateZ(10px)" }}>
                  <Img
                    src={staticFile("assets_library/gameplay/gusion_skill2_ingame.jpg")}
                    style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 30%" }}
                  />
                  <div style={{ position: "absolute", inset: 0, boxShadow: "inset 0 0 45px rgba(93, 63, 211, 0.4)" }} />
                </div>

                {/* Floating Safe Form ID Pill (Right, z: 95px) */}
                <div
                  style={{
                    position: "absolute",
                    bottom: 60,
                    right: -30,
                    transform: "translateZ(95px) rotateY(-10deg)",
                    backgroundColor: bgCard,
                    borderRadius: 24,
                    border: `2px solid ${emeraldSuccess}`,
                    padding: "16px 24px",
                    boxShadow: "0 20px 45px rgba(16, 185, 129, 0.2)",
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    zIndex: 35,
                  }}
                >
                  <div style={{ width: 42, height: 42, borderRadius: 12, backgroundColor: emeraldLight, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={emeraldSuccess} strokeWidth="3">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/>
                    </svg>
                  </div>
                  <div>
                    <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 24, color: emeraldSuccess }}>CUKUP USER ID & ZONE ID</div>
                    <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 13, fontWeight: 800, color: textDark }}>100% Tanpa Password Akun</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ================================================================ */}
          {/* SCENE 4: WEEKLY DIAMOND PASS 3D BUNDLE (Frame 1100 - 1460)       */}
          {/* ================================================================ */}
          {frame >= 1095 && frame < 1462 && (
            <div
              style={{
                transformStyle: "preserve-3d",
                transform: `translateY(${s4Y + s4ExitY + floatY}px) scale(${s4Scale})`,
                opacity: s4ExitOpacity,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <div style={{ textAlign: "center", marginBottom: 20, transform: "translateZ(40px)" }}>
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    backgroundColor: "#FFFBEB",
                    border: `1.5px solid ${accentGold}`,
                    padding: "6px 20px",
                    borderRadius: 20,
                    marginBottom: 12,
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={accentGold} strokeWidth="2.5">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                  </svg>
                  <span style={{ fontFamily: "'Chakra Petch', sans-serif", fontSize: 14, fontWeight: 700, color: accentGold, letterSpacing: "0.08em" }}>
                    PAKET PALING HEMAT SE-INDONESIA
                  </span>
                </div>
                <h1 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 66, lineHeight: 1.02, letterSpacing: "0.02em" }}>
                  WEEKLY DIAMOND PASS, <br />
                  <span style={{ color: kyuPurple }}>MODAL 27 RIBUAN TOTAL DAPAT 220 DIAMOND!</span>
                </h1>
              </div>

              {/* Showcase Card with 3D Weekly Pass */}
              <div
                style={{
                  position: "relative",
                  width: 860,
                  height: 640,
                  backgroundColor: bgCard,
                  borderRadius: 38,
                  border: `2.5px solid ${kyuPurple}`,
                  boxShadow: "0 35px 90px rgba(93, 63, 211, 0.2)",
                  padding: "40px",
                  display: "flex",
                  gap: 36,
                  alignItems: "center",
                  transformStyle: "preserve-3d",
                }}
              >
                {/* Left: 3D Official Weekly Pass Bundle Cutout */}
                <div
                  style={{
                    width: 320,
                    height: 320,
                    backgroundColor: kyuLavender,
                    borderRadius: 30,
                    border: "2px solid #D8B4FE",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transform: "translateZ(30px)",
                  }}
                >
                  <Img
                    src={staticFile("assets_library/items/weekly_diamond_pass_3d.png")}
                    style={{ width: "90%", height: "90%", objectFit: "contain", filter: "drop-shadow(0 15px 30px rgba(93,63,211,0.35))" }}
                  />
                </div>

                {/* Right: Pricing & Benefits */}
                <div style={{ flex: 1, transform: "translateZ(30px)" }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 14 }}>
                    <span style={{ fontFamily: "'Anton', sans-serif", fontSize: 56, color: kyuPurple }}>Rp 27.500</span>
                    <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 22, color: textMuted, textDecoration: "line-through", fontWeight: 700 }}>
                      Rp 35.000
                    </span>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 18 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 16, fontWeight: 800 }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={emeraldSuccess} strokeWidth="3">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      <span>Klaim 30 Diamond Tiap Hari (Total 220 💎)</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 16, fontWeight: 800 }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={emeraldSuccess} strokeWidth="3">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      <span>Bonus Starlight Point & Choice Bundle</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 16, fontWeight: 800 }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={emeraldSuccess} strokeWidth="3">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      <span>Hemat hingga 70% Dibanding Beli Biasa</span>
                    </div>
                  </div>

                  {/* Interactive Button */}
                  <div
                    style={{
                      marginTop: 24,
                      backgroundColor: s4IsClicking ? kyuDark : kyuPurple,
                      color: "#FFFFFF",
                      borderRadius: 20,
                      padding: "16px 28px",
                      fontSize: 20,
                      fontWeight: 900,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      transform: s4IsClicking ? "scale(0.96)" : "scale(1)",
                    }}
                  >
                    <span>Pilih Weekly Pass</span>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="3">
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                      <polyline points="12 5 19 12 12 19"></polyline>
                    </svg>
                  </div>
                </div>

                {/* Animated Mouse Pointer */}
                <div
                  style={{
                    position: "absolute",
                    left: s4CursorX,
                    top: s4CursorY,
                    zIndex: 45,
                    transform: `scale(${s4IsClicking ? 0.88 : 1.0})`,
                  }}
                >
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="#120A2E" stroke="#FFFFFF" strokeWidth="2">
                    <path d="M3 3l7 18 3-7 7-3L3 3z"/>
                  </svg>
                </div>
              </div>
            </div>
          )}

          {/* ================================================================ */}
          {/* SCENE 5: INSTANT QRIS & 0.8S AUTO INJECT (Frame 1460 - 1880)     */}
          {/* ================================================================ */}
          {frame >= 1455 && frame < 1882 && (
            <div
              style={{
                transformStyle: "preserve-3d",
                transform: `translateY(${s5Y + s5ExitY + floatY}px) scale(${s5Scale})`,
                opacity: s5ExitOpacity,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <div style={{ textAlign: "center", marginBottom: 20, transform: "translateZ(40px)" }}>
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    backgroundColor: emeraldLight,
                    border: `1.5px solid ${emeraldSuccess}`,
                    padding: "6px 20px",
                    borderRadius: 20,
                    marginBottom: 12,
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={emeraldSuccess} strokeWidth="3">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  <span style={{ fontFamily: "'Chakra Petch', sans-serif", fontSize: 14, fontWeight: 700, color: emeraldSuccess, letterSpacing: "0.08em" }}>
                    PROSES SATSET HITUNGAN DETIK
                  </span>
                </div>
                <h1 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 66, lineHeight: 1.02, letterSpacing: "0.02em" }}>
                  SCAN QRIS SATSET SEMUA E-WALLET, <br />
                  <span style={{ color: kyuPurple }}>DIAMOND LANGSUNG MASUK DALAM 0.8 DETIK!</span>
                </h1>
              </div>

              {/* Split QRIS & Success Stage */}
              <div style={{ width: 860, display: "flex", gap: 24, alignItems: "center", transformStyle: "preserve-3d" }}>
                {/* QRIS Scanner */}
                <div
                  style={{
                    flex: 1,
                    backgroundColor: bgCard,
                    borderRadius: 30,
                    border: "2px solid #E9D5FF",
                    padding: "26px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    boxShadow: "0 20px 45px rgba(93, 63, 211, 0.12)",
                  }}
                >
                  <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 16, fontWeight: 900, color: kyuDark, marginBottom: 12 }}>
                    QRIS SEMUA BANK & E-WALLET
                  </div>
                  <div
                    style={{
                      width: 200,
                      height: 200,
                      backgroundColor: "#111827",
                      borderRadius: 22,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    <svg width="125" height="125" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2">
                      <rect x="2" y="2" width="8" height="8" rx="1"></rect>
                      <rect x="14" y="2" width="8" height="8" rx="1"></rect>
                      <rect x="14" y="14" width="8" height="8" rx="1"></rect>
                      <rect x="2" y="14" width="8" height="8" rx="1"></rect>
                    </svg>
                    <div
                      style={{
                        position: "absolute",
                        left: 10,
                        right: 10,
                        top: 100 + scanLaserY,
                        height: 4,
                        backgroundColor: "#00F0FF",
                        boxShadow: "0 0 14px #00F0FF, 0 0 24px #00F0FF",
                      }}
                    />
                  </div>
                </div>

                {/* Right: Success Notification */}
                <div style={{ flex: 1.15, display: "flex", flexDirection: "column", gap: 16 }}>
                  <div
                    style={{
                      backgroundColor: bgCard,
                      borderRadius: 26,
                      border: `2px solid ${emeraldSuccess}`,
                      padding: "24px",
                      boxShadow: "0 18px 40px rgba(16, 185, 129, 0.16)",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontFamily: "'Chakra Petch', sans-serif", fontSize: 16, fontWeight: 700, color: emeraldSuccess, display: "flex", alignItems: "center", gap: 6 }}>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={emeraldSuccess} strokeWidth="3">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                        TRANSAKSI BERHASIL
                      </span>
                      <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: textMuted, fontWeight: 800 }}>⚡ 0.8 DETIK</span>
                    </div>
                    <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 32, letterSpacing: "0.02em", marginTop: 8 }}>
                      Weekly Diamond Pass Aktif
                    </div>
                    <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 15, color: textMuted, marginTop: 4 }}>
                      User ID: <span style={{ fontWeight: 800, color: textDark }}>84920194 (2041)</span> • Otomatis masuk
                    </div>
                  </div>

                  <div style={{ backgroundColor: bgCard, borderRadius: 22, border: "1.5px solid #E2E8F0", padding: "16px 20px" }}>
                    <span style={{ fontSize: 13, color: textMuted, fontWeight: 700 }}>Metode Terintegrasi:</span>
                    <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 16, fontWeight: 900, color: textDark, marginTop: 4 }}>
                      BCA • Mandiri • BRI • Dana • GoPay • ShopeePay
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ================================================================ */}
          {/* SCENE 6: VICTORY SCOREBOARD & OUTRO CTA (Frame 1880 - 2400)      */}
          {/* ================================================================ */}
          {frame >= 1875 && (
            <div
              style={{
                transformStyle: "preserve-3d",
                transform: `translateY(${s6Y + floatY}px) scale(${s6Scale})`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <div style={{ textAlign: "center", marginBottom: 20, transform: "translateZ(40px)" }}>
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    backgroundColor: emeraldLight,
                    border: `1.5px solid ${emeraldSuccess}`,
                    padding: "6px 20px",
                    borderRadius: 20,
                    marginBottom: 12,
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={emeraldSuccess} strokeWidth="3">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  <span style={{ fontFamily: "'Chakra Petch', sans-serif", fontSize: 14, fontWeight: 700, color: emeraldSuccess, letterSpacing: "0.08em" }}>
                    MAIN TENANG • AKUN AMAN TERPERCAYA
                  </span>
                </div>
                <h1 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 66, lineHeight: 1.02, letterSpacing: "0.02em" }}>
                  JAGA AKUN KESAYANGANMU TETAP AMAN! <br />
                  <span style={{ color: kyuPurple }}>TOP UP RESMI HANYA DI KYUSTORE.COM!</span>
                </h1>
              </div>

              {/* Showcase Card with Real Victory Scoreboard */}
              <div
                style={{
                  position: "relative",
                  width: 860,
                  height: 640,
                  backgroundColor: "rgba(255, 255, 255, 0.94)",
                  borderRadius: 38,
                  border: `2.5px solid ${kyuPurple}`,
                  boxShadow: "0 35px 90px rgba(93, 63, 211, 0.2)",
                  transformStyle: "preserve-3d",
                }}
              >
                {/* Real Gameplay Screenshot: Victory Scoreboard */}
                <div style={{ position: "absolute", inset: 16, borderRadius: 28, overflow: "hidden", transform: "translateZ(10px)" }}>
                  <Img
                    src={staticFile("assets_library/gameplay/mlbb_victory_scoreboard.jpg")}
                    style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 35%" }}
                  />
                  <div style={{ position: "absolute", inset: 0, boxShadow: "inset 0 0 45px rgba(93, 63, 211, 0.4)" }} />
                </div>

                {/* Massive Outro CTA Button (z: 110px) */}
                <div
                  style={{
                    position: "absolute",
                    bottom: 40,
                    left: "50%",
                    transform: `translateX(-50%) translateZ(110px) scale(${s6IsClicking ? 0.94 : 1.0})`,
                    backgroundColor: kyuDark,
                    color: "#FFFFFF",
                    border: `2.5px solid ${accentGold}`,
                    borderRadius: 32,
                    padding: "20px 48px",
                    display: "flex",
                    alignItems: "center",
                    gap: 20,
                    boxShadow: "0 20px 50px rgba(18, 10, 46, 0.35)",
                    zIndex: 40,
                  }}
                >
                  <div>
                    <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 32, letterSpacing: "0.03em" }}>
                      TOP UP RESMI SEKARANG JUGA
                    </div>
                    <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 16, color: accentGold, fontWeight: 700 }}>
                      Kunjungi website: kyustore.com
                    </div>
                  </div>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={accentGold} strokeWidth="3">
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                    <polyline points="12 5 19 12 12 19"></polyline>
                  </svg>
                </div>

                {/* Pointer Cursor */}
                <div
                  style={{
                    position: "absolute",
                    left: s6CursorX,
                    top: s6CursorY,
                    zIndex: 45,
                    transform: `scale(${s6IsClicking ? 0.88 : 1.0})`,
                  }}
                >
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="#120A2E" stroke="#FFFFFF" strokeWidth="2">
                    <path d="M3 3l7 18 3-7 7-3L3 3z"/>
                  </svg>
                </div>
              </div>
            </div>
          )}

          {/* 18 Multi-Depth Atmospheric Particles */}
          <AtmosphericParticles frame={frame} count={18} />
        </div>
      </div>

      {/* ========================================================================= */}
      {/* CORNER FOOTER TICKER (Safe Zone Bottom 80px)                              */}
      {/* ========================================================================= */}
      <div
        style={{
          position: "absolute",
          bottom: 80,
          left: 90,
          right: 90,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: bgCard,
          border: "1.5px solid #E2E8F0",
          borderRadius: 20,
          padding: "12px 28px",
          boxShadow: "0 6px 20px rgba(0,0,0,0.04)",
          zIndex: 50,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, fontFamily: "'Rajdhani', sans-serif", fontSize: 14, fontWeight: 800, color: textMuted }}>
          <span style={{ color: kyuPurple }}>METODE RESMI:</span>
          <span>QRIS • BCA • MANDIRI • BRI • DANA • GOPAY • SHOPEEPAY</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontFamily: "'Chakra Petch', sans-serif", fontSize: 13, fontWeight: 700, color: emeraldSuccess }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={emeraldSuccess} strokeWidth="3">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
          <span>MOONTON DIRECT INJECT</span>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* DYNAMIC SUBTITLES: RAISED TO OPTIMAL SAFE BAND (bottom: 400px)            */}
      {/* ========================================================================= */}
      <DynamicSubtitle frame={frame} subtitles={SUBTITLES} bottom={400} />
    </AbsoluteFill>
  );
};
